// Page layout, phase 1 (AdminLayout.dc.html with its Tweak at "Phase 1: settings"; decision 25): which
// sections show on a product's home page and its week (or recording) pages, and in what order, with a
// drawn preview at laptop and phone width, Save draft and Publish. The Funding Engine, Profit On Your
// Terms, The Property Accelerator and Property Cashflow For Life have pages that read a layout in
// phase 1 (model.js layoutPages). Members
// only ever read a published version (hub.my_layout), so a draft changes nothing they see. Owner and
// admin (the shell; layouts are theirs alone). Phase 2's section builder isn't built.
import { useEffect, useMemo, useState } from 'react'
import { PageHeader, Card, Button, ButtonLink, Flash, Loading, Problem, useLoad, adminPath, InfoNote } from '../ui/index.js'
import { Link } from '../../lib/router.jsx'
import { clearLayoutCache } from '../../product/Layout.jsx'
import { LAYOUT } from '../products/copy.js'
import { layoutPages, editableSections, sectionWords, defaultSections, sameOrder } from '../products/model.js'
import { loadLayouts, saveDraft, publishLayout } from '../products/data.js'
import { Mark } from '../products/bits.jsx'
import { LayoutPreview } from '../products/LayoutPreview.jsx'
import '../../styles/admin-products.css'

function HeadCrumbs({ name }) {
  return (
    <span className="ap-eyebrow-crumbs">
      <Link to={adminPath.products()}>{LAYOUT.crumbs}</Link>
      {name && <><span aria-hidden="true">/</span><span>{name}</span></>}
    </span>
  )
}

export default function Layout({ api, route, me }) {
  const slug = route.params.slug
  const data = useLoad(() => loadLayouts(api, slug), [api, slug])
  const p = data.data?.product
  const pages = layoutPages(p)

  if (data.status === 'loading') return <div className="ap-page"><PageHeader eyebrow={<HeadCrumbs />} title={LAYOUT.title} /><Loading /></div>
  if (data.status === 'error') return <div className="ap-page"><PageHeader eyebrow={<HeadCrumbs />} title={LAYOUT.title} /><Problem error={data.error} onRetry={data.reload} /></div>
  if (!p || !pages) {
    return (
      <div className="ap-page">
        <PageHeader eyebrow={<HeadCrumbs name={p?.name} />} title={LAYOUT.title} />
        <Card>
          <p className="ad-lede">{p ? LAYOUT.none(p.name) : LAYOUT.notFound}</p>
          {p && <p className="ap-step-line">{LAYOUT.noneWhy}</p>}
          <p><ButtonLink to={adminPath.products()} size="sm">{LAYOUT.back}</ButtonLink></p>
        </Card>
      </div>
    )
  }
  return <LayoutEditor api={api} product={p} pages={pages} loaded={data.data} actorId={me?.who?.person_id} />
}

function LayoutEditor({ api, product: p, pages, loaded, actorId }) {
  const [layouts, setLayouts] = useState(() => loaded.layouts)
  const [versions, setVersions] = useState(() => loaded.versions)
  const published = page => {
    const l = layouts.find(x => x.page === page)
    return versions.find(v => v.id === l?.published_version_id)?.sections || null
  }
  const startList = page => {
    const l = layouts.find(x => x.page === page)
    return editableSections(p, page, l?.has_draft ? l.draft_sections : (published(page) || defaultSections(p, page)))
  }
  const [lists, setLists] = useState(() => Object.fromEntries(pages.map(pg => [pg.key, startList(pg.key)])))
  const [tab, setTab] = useState(pages[0].key)
  const [device, setDevice] = useState('desktop')
  const [flash, setFlash] = useState(null)
  const [busy, setBusy] = useState(false)

  const list = lists[tab]
  const layout = layouts.find(x => x.page === tab) || null
  const live = editableSections(p, tab, published(tab) || defaultSections(p, tab))
  const draft = Boolean(layout?.has_draft) || !sameOrder(list, live)
  const shownCount = list.filter(s => s.shown).length
  const words = key => sectionWords(p, key)
  const pageInfo = pages.find(pg => pg.key === tab)

  useEffect(() => { setFlash(null) }, [tab])

  const setList = next => { setFlash(null); setLists(s => ({ ...s, [tab]: next })) }
  const swap = (i, j) => {
    if (j < 0 || j >= list.length) return
    const a = list.slice()
    ;[a[i], a[j]] = [a[j], a[i]]
    setList(a)
  }
  const toggle = key => setList(list.map(s => (s.key === key && !s.fixed ? { ...s, shown: !s.shown } : s)))

  async function run(fn, done) {
    if (busy) return
    setBusy(true)
    setFlash(null)
    try {
      await fn()
      setFlash({ text: done })
    } catch (e) {
      setFlash({ text: e.message, problem: true })
    } finally {
      setBusy(false)
    }
  }
  const keepLayout = row => setLayouts(ls => [...ls.filter(l => l.page !== row.page), row])
  const onSave = () => run(async () => keepLayout(await saveDraft(api, p, tab, layout, list, actorId)), LAYOUT.draftSaved)
  const onPublish = () => {
    if (shownCount === 0) { setFlash({ text: LAYOUT.nothingShown, problem: true }); return }
    run(async () => {
      const { layout: row, version } = await publishLayout(api, p, tab, layout, list, actorId)
      keepLayout(row)
      setVersions(vs => [...vs.filter(v => v.id !== version.id), version])
      // a member page open in this tab reads the new layout next time
      clearLayoutCache()
    }, LAYOUT.published)
  }
  const onDefault = () => {
    setLists(s => ({ ...s, [tab]: editableSections(p, tab, defaultSections(p, tab)) }))
    setFlash({ text: LAYOUT.defaultBack })
  }

  const lookName = p.look === 'funding_engine' ? 'The Funding Engine' : p.name
  return (
    <div className="ap-page ap-layout">
      <PageHeader eyebrow={<HeadCrumbs name={p.name} />} title={LAYOUT.title} docTitle={`Admin: ${p.name} page layout`}
        actions={(
          <span className="ap-layout-actions">
            <span className="ap-status" role="status">{draft ? LAYOUT.statusDraft : LAYOUT.statusPublished}</span>
            <Button onClick={onSave} disabled={busy}>{LAYOUT.saveDraft}</Button>
            <Button kind="ink" onClick={onPublish} disabled={busy} aria-disabled={shownCount === 0 || undefined}>{LAYOUT.publish}</Button>
          </span>
        )} />
      {flash && <Flash tone={flash.problem ? 'problem' : 'done'}>{flash.text}</Flash>}

      <div className="ap-layout-bar">
        <div className="ap-layout-tabs" role="tablist" aria-label={LAYOUT.title}>
          {pages.map(pg => (
            <button key={pg.key} type="button" role="tab" className="ad-tab" aria-selected={tab === pg.key} onClick={() => setTab(pg.key)}>{pg.label}</button>
          ))}
        </div>
        <div className="ap-devices" role="group" aria-label={LAYOUT.previewSize}>
          {[['desktop', LAYOUT.desktop], ['phone', LAYOUT.phone]].map(([key, label]) => (
            <button key={key} type="button" className="ap-device" aria-pressed={device === key} onClick={() => setDevice(key)}>{label}</button>
          ))}
        </div>
      </div>

      <InfoNote>{LAYOUT.info}</InfoNote>

      <div className="ap-layout-grid" role="tabpanel">
        <div className="ap-layout-left">
          <section className="ap-sections" aria-labelledby="ap-sections-h">
            <div className="ap-sections-head">
              <h2 className="ap-sections-h" id="ap-sections-h">{pageInfo.list}</h2>
              <button type="button" className="ap-text-btn ap-underline" onClick={onDefault} disabled={busy}>{LAYOUT.backToDefault}</button>
            </div>
            <ol className="ap-sec-list">
              {list.map((s, i) => {
                const w = words(s.key)
                return (
                  <li key={s.key} className={`ap-sec${s.shown ? '' : ' ap-sec--hidden'}`}>
                    <span className="ap-num" aria-hidden="true">{i + 1}</span>
                    <span className="ap-sec-text"><span className="ap-sec-name">{w.name}</span><span className="ap-sec-what">{w.what}</span></span>
                    <button type="button" className="ap-icon-btn" aria-label={LAYOUT.moveUp(w.name)} disabled={i === 0} onClick={() => swap(i, i - 1)}><Mark name="up" /></button>
                    <button type="button" className="ap-icon-btn" aria-label={LAYOUT.moveDown(w.name)} disabled={i === list.length - 1} onClick={() => swap(i, i + 1)}><Mark name="down" /></button>
                    {s.fixed
                      ? <span className="ap-always">{LAYOUT.always}</span>
                      : (
                        <button type="button" role="switch" className="ad-switch" aria-checked={s.shown} aria-label={s.shown ? LAYOUT.hide(w.name) : LAYOUT.show(w.name)} onClick={() => toggle(s.key)}>
                          <span className="ad-switch-knob" />
                        </button>
                      )}
                  </li>
                )
              })}
            </ol>
            <p className="ap-sections-foot">{LAYOUT.listFoot}</p>
          </section>
          <LookPanel product={p} lookName={lookName} />
        </div>
        <LayoutPreview product={p} page={tab} sections={list} words={words} phone={device === 'phone'} />
      </div>
    </div>
  )
}

function LookPanel({ product, lookName }) {
  const look = product.look === 'funding_engine' ? 'fe' : product.look === 'accelerator' ? 'acc' : product.look === 'poyt' ? 'poyt' : 'master'
  const fonts = { fe: ['ANTON', 'and Archivo'], acc: ['Inter Tight', 'and Inter'], poyt: ['Archivo', 'and IBM Plex Sans'], master: ['Manrope', ''] }[look]
  return (
    <section className="ad-card ap-look-panel" aria-labelledby="ap-look-h">
      <h2 className="ap-look-h" id="ap-look-h">{LAYOUT.lookH2}</h2>
      <p className="ap-look-line">{LAYOUT.lookLine(lookName)}</p>
      <div className={`ap-look-sw ap-look-sw--${look}`}>
        <span className="ap-look-chip ap-look-chip--page" title="Page" />
        <span className="ap-look-chip ap-look-chip--card" title="Card" />
        <span className="ap-look-chip ap-look-chip--accent" title="Accent" />
        <span className="ap-look-chip ap-look-chip--text" title="Text" />
        <span className="ap-look-font">{fonts[0]}</span>
        {fonts[1] && <span className="ap-look-body">{fonts[1]}</span>}
      </div>
    </section>
  )
}
